import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { selectPatientDetails } from "../../store/selector";
import ACTIONS from "../../store/action";
import { fontWeights, sizes } from "../../constant/styles";
import useStandardizedTracking, {
  EventNames,
  ContentTypes,
  FeatureGroups
} from "../../utils/pendo/useStandardizedTracking";
import {
  BRAND_BLUE,
  GRAY_50,
  GRAY_100,
  GRAY_200,
  GRAY_300,
  LIGHT_BLUE_6,
  PRIMARY_600,
  WHITE,
  WHITE_GRAY
} from "../../constant/colors";
import { ReactComponent as ConsentIcon } from "../../assets/svg/consent-icon.svg";
import { ReactComponent as CaretRightIcon } from "../../assets/svg/Caret.svg";
import ConsentQuickActions from "./ConsentQuickActions";
import GeneralConsentForm from "./ConsentQuickActionForms/GeneralConsentForm";
import RequestConsentForm from "./ConsentQuickActionForms/RequestConsentForm";
import ConsentHistoryContent from "./ConsentHistoryContent";
import Banner from "../sharedComponents/Banner";
import { getConsentHistoryAPI, registerConsentAPI, revokeConsentAPI } from "../../api/api";
import { useConsentStatusContext } from "../../context/ConsentStatusContext";
import { CONSENT_ACTION_TYPES, QUICK_ACTION_KEYS, CONSENT_EVENT_TYPES } from "../../constant/consent";

const RegisterConsentForm = ({ patientId, ...props }) => (
  <GeneralConsentForm
    {...props}
    actionType={CONSENT_ACTION_TYPES.RECEIVED}
    onSubmit={(formData) => registerConsentAPI(patientId, formData)}
  />
);

const RevokeConsentForm = ({ patientId, ...props }) => (
  <GeneralConsentForm
    {...props}
    actionType={CONSENT_ACTION_TYPES.REVOKED}
    onSubmit={(formData) => revokeConsentAPI(patientId, formData)}
  />
);

const MENU_KEYS = {
  HISTORY: "history"
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${GRAY_50};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background-color: ${PRIMARY_600};
  color: ${WHITE_GRAY};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${WHITE_GRAY};
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: 15px;
  border: none;
  cursor: pointer;
  color: ${WHITE};
  padding: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const MenuContainer = styled.div`
  padding: 0px 20px;
  border-bottom: 1px solid ${GRAY_200};
  display: flex;
  align-items: center;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 6px;
  border-bottom: ${(props) => (props.isActive ? `2px solid ${BRAND_BLUE}` : "2px solid transparent")};
  color: ${(props) => (props.isActive ? PRIMARY_600 : GRAY_300)};
  font-weight: ${(props) => (props.isActive ? fontWeights.bold : fontWeights.regular)};
  font-size: ${sizes.small};
  line-height: 1.5em;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${PRIMARY_600};
    border-bottom-color: ${(props) => (props.isActive ? BRAND_BLUE : LIGHT_BLUE_6)};
  }
`;

const MainContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HistoryAndActionsContainer = styled.div`
  flex: 1;
  margin: 20px 17px 0px 17px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HistoryContainer = styled.div`
  flex: 1;
  background-color: ${WHITE};
  border: 1px solid ${GRAY_100};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HistoryScrollableContent = styled.div`
  flex: 1;
  padding-right: 16px;
  margin: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;

  /* Custom scrollbar styling - matches Figma design */
  &::-webkit-scrollbar {
    width: 5px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background-color: ${GRAY_300};
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const QuickActionSection = styled.div`
  flex-shrink: 0;
  background-color: ${GRAY_100};
  display: flex;
  flex-direction: column;
`;

const QuickActionContentContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

/**
 * Reusable consent management content component
 * Can be used inside Modal, Drawer, or any other container
 */
const ConsentManagementContent = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const patientDetails = useSelector(selectPatientDetails);
  const { name: patientName, id: patientId } = patientDetails || {};
  const { refreshConsentStatus } = useConsentStatusContext();
  const { track } = useStandardizedTracking();

  const [activeMenuItem, setActiveMenuItem] = useState(MENU_KEYS.HISTORY);
  const [selectedQuickAction, setSelectedQuickAction] = useState(null);
  const [consentHistory, setConsentHistory] = useState([]);
  const [canViewOrDownloadAttachments, setCanViewOrDownloadAttachments] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [banner, setBanner] = useState(null); // { type: 'success' | 'warning' | 'error', message: string }

  const fetchConsentHistory = async () => {
    if (!patientId) return;

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const response = await getConsentHistoryAPI(patientId);
      // Backend now returns { data: events[], canViewOrDownload: boolean }
      const events = response.data?.data || response.data || [];
      const canViewOrDownload = response.data?.canViewOrDownload || false;

      const formattedEvents = events
        .filter((event) => event.type !== CONSENT_EVENT_TYPES.LINK_REVOKED)
        .map((event) => ({
          id: event.id,
          type: event.type,
          userName: event.userName || "",
          createdAt: event.createdAt,
          effectiveDate: event.effectiveDate,
          via: event.via,
          doneBy: event.doneBy,
          note: event.note,
          attachmentDetails: event.attachmentDetails,
          contactInfo: event.contactInfo || null,
          patientName
        }));

      setConsentHistory(formattedEvents);
      setCanViewOrDownloadAttachments(canViewOrDownload);
    } catch (error) {
      setHistoryError(error);
      setConsentHistory([]);
      setCanViewOrDownloadAttachments(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchConsentHistory();
  }, [patientId]);

  // Step 2: Track popup impression after consent history data is fetched
  useEffect(() => {
    if (!historyLoading && patientId) {
      track(EventNames.POP_UP_IMPRESSION, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: ContentTypes.CONSENT,
        contentKey: "consent_management_popup",
        layout: "popup",
        additionalInfo: {
          popup_title: "Manage Consent",
          available_actions: ["request_consent", "record_consent", "revoke_consent"],
          consent_history_loaded: !historyError,
          consent_history_error: !!historyError
        }
      });
    }
  }, [historyLoading, patientId, historyError, track]);

  const handleConsentFormCancel = () => {
    setSelectedQuickAction(null);
    setBanner((prev) => (prev?.type === "error" ? null : prev));
    // Don't clear success/warning banners here - they should persist after form closes
  };

  const handleConsentFormSave = (partialSuccessInfo = null) => {
    // Determine which banner to show
    if (selectedQuickAction === QUICK_ACTION_KEYS.REQUEST) {
      // For REQUEST action: check if it was partial success
      // partialSuccessInfo will have a 'message' property if partial, or be null if full success
      if (partialSuccessInfo?.message) {
        // Partial success - show warning banner
        setBanner({ type: "warning", message: partialSuccessInfo.message });
        setSelectedQuickAction(null);
      } else {
        // Full success - show success banner
        setBanner({ type: "success", message: t("consent.forms.success_messages.request") });
        handleConsentFormCancel();
      }
    } else {
      // For REGISTER/REVOKE: always show success banner (they only call onSave on success)
      const messageKey =
        selectedQuickAction === QUICK_ACTION_KEYS.REGISTER
          ? "consent.forms.success_messages.register"
          : "consent.forms.success_messages.revoke";

      const bannerMessage = t(messageKey);
      setBanner({ type: "success", message: bannerMessage });

      // Step 6: Track banner impression for Register Consent success
      if (selectedQuickAction === QUICK_ACTION_KEYS.REGISTER) {
        track(EventNames.BANNER_IMPRESSION, {
          pageKey: "patient_overview",
          featureGroup: FeatureGroups.ECONSENT,
          contentType: ContentTypes.CONSENT,
          contentKey: "consent_registered_confirmation",
          layout: "banner",
          additionalInfo: {
            banner_message: bannerMessage
          }
        });
      }

      handleConsentFormCancel();
    }

    fetchConsentHistory();
    refreshConsentStatus();

    dispatch(ACTIONS.fetchPatientNotesAction(patientId));
    dispatch(ACTIONS.fetchPatientAttachmentsCountAction(patientId));
  };

  const handleConsentFormError = (error) => {
    if (error) {
      setBanner({ type: "error", message: error });
    } else {
      setBanner(null);
    }
  };

  const menuConfig = [
    {
      key: MENU_KEYS.HISTORY,
      label: t("consent.drawer.consent_history"),
      renderContent: () => (
        <ConsentHistoryContent
          historyLoading={historyLoading}
          historyError={historyError}
          consentHistory={consentHistory}
          canViewOrDownloadAttachments={canViewOrDownloadAttachments}
          onRetry={fetchConsentHistory}
        />
      )
    }
  ];

  const quickActionRenderers = {
    [QUICK_ACTION_KEYS.REQUEST]: () => (
      <RequestConsentForm
        onCancel={handleConsentFormCancel}
        onSave={handleConsentFormSave}
        onError={handleConsentFormError}
        patientDetails={patientDetails}
      />
    ),
    [QUICK_ACTION_KEYS.REGISTER]: () => (
      <RegisterConsentForm
        patientId={patientId}
        onCancel={handleConsentFormCancel}
        onSave={handleConsentFormSave}
        onError={handleConsentFormError}
      />
    ),
    [QUICK_ACTION_KEYS.REVOKE]: () => (
      <RevokeConsentForm
        patientId={patientId}
        onCancel={handleConsentFormCancel}
        onSave={handleConsentFormSave}
        onError={handleConsentFormError}
      />
    )
  };

  return (
    <Container>
      <div style={{ flexShrink: 0 }}>
        <Header>
          <HeaderTitle>
            <ConsentIcon style={{ fontSize: sizes.xlarge }} />
            <span id="consent-drawer-title">{t("consent.drawer.title", { patientName })}</span>
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <CaretRightIcon />
          </CloseButton>
        </Header>

        <Banner type={banner?.type} message={banner?.message} onClose={() => setBanner(null)} />

        <MenuContainer>
          {menuConfig.map((item) => (
            <MenuItem key={item.key} isActive={activeMenuItem === item.key} onClick={() => setActiveMenuItem(item.key)}>
              {item.label}
            </MenuItem>
          ))}
        </MenuContainer>
      </div>

      <MainContentSection>
        <HistoryAndActionsContainer>
          <HistoryContainer>
            <HistoryScrollableContent>
              {menuConfig.find((item) => item.key === activeMenuItem)?.renderContent?.()}
            </HistoryScrollableContent>
          </HistoryContainer>

          <ConsentQuickActions
            onAction={(actionKey) => {
              const isClosing = selectedQuickAction === actionKey;

              if (!isClosing) {
                // Opening a NEW action - clear banner and set new action
                setBanner(null);
                setSelectedQuickAction(actionKey);
              } else {
                // Closing the current action
                setSelectedQuickAction(null);
              }
            }}
            selectedAction={selectedQuickAction}
            hasContent={!!selectedQuickAction}
          />
        </HistoryAndActionsContainer>
      </MainContentSection>

      {selectedQuickAction && quickActionRenderers[selectedQuickAction] && (
        <QuickActionSection>
          <QuickActionContentContainer>{quickActionRenderers[selectedQuickAction]()}</QuickActionContentContainer>
        </QuickActionSection>
      )}
    </Container>
  );
};

export default ConsentManagementContent;
